import { useCallback, useEffect, useState } from 'react';
import {
  getCandidateProfile,
  updateCandidateProfile,
} from '../services/candidate-profile.service';
import type {
  CandidateProfile,
  UpdateCandidateProfileInput,
} from '../models/candidate-profile.types';

interface UseCandidateProfileResult {
  profile: CandidateProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  loadError: unknown;
  saveError: unknown;
  savedAt: Date | null;
  reload: () => Promise<void>;
  save: (input: UpdateCandidateProfileInput) => Promise<CandidateProfile>;
}

export function useCandidateProfile(): UseCandidateProfileResult {
  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setProfile(await getCandidateProfile());
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(async (input: UpdateCandidateProfileInput) => {
    setIsSaving(true);
    setSaveError(null);
    setSavedAt(null);
    try {
      const updated = await updateCandidateProfile(input);
      setProfile(updated);
      setSavedAt(new Date());
      return updated;
    } catch (error) {
      setSaveError(error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    profile,
    isLoading,
    isSaving,
    loadError,
    saveError,
    savedAt,
    reload,
    save,
  };
}
